import { useEffect, useMemo, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScheduleBuilder, type ScheduleOutput } from './ScheduleBuilder';
import type { TriggerCreateInput } from '@/lib/scheduler-api';
import { Check, ChevronLeft, ChevronRight, Layers, Plus, X } from 'lucide-react';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { getCapabilityColors } from '@/lib/capability-colors';
import {
    CapabilityPickerDrawer,
    type CapabilityPickerItem,
} from '@/components/agent-config/CapabilityPickerDrawer';

interface CreateTriggerWizardProps {
    initial?: Partial<TriggerCreateInput>;
    onSave: (input: TriggerCreateInput) => Promise<void>;
    saving?: boolean;
}

interface SelectedCapability {
    ref: string;
    name: string;
    type: string;
    description?: string;
}

interface SchedulerCapabilityItem extends CapabilityPickerItem {
    methodNames?: string[];
}

type TriggerWizardStep = 'details' | 'schedule' | 'payload' | 'summary';

const WIZARD_STEPS: Array<{
    id: TriggerWizardStep;
    title: string;
    description: string;
}> = [
    {
        id: 'details',
        title: 'Details & Policies',
        description: 'Name, target, and execution policies',
    },
    {
        id: 'schedule',
        title: 'Schedule',
        description: 'Define when this trigger should run',
    },
    {
        id: 'payload',
        title: 'Input Payload',
        description: 'Optional JSON payload sent on invocation',
    },
    {
        id: 'summary',
        title: 'Summary & Confirm',
        description: 'Review and confirm your trigger changes',
    },
];

const inputClassName =
    'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm';

export function CreateTriggerWizard({ initial, onSave, saving }: CreateTriggerWizardProps) {
    const [currentStep, setCurrentStep] = useState<TriggerWizardStep>('details');
    const [name, setName] = useState(initial?.name ?? '');
    const [targetRef, setTargetRef] = useState(initial?.targetRef ?? '');
    const [targetMethod, setTargetMethod] = useState(initial?.targetMethod ?? '');
    const [concurrencyPolicy, setConcurrencyPolicy] = useState(initial?.concurrencyPolicy ?? 'forbid');
    const [misfirePolicy, setMisfirePolicy] = useState(initial?.misfirePolicy ?? 'fire_once_now');
    const [payloadStr, setPayloadStr] = useState(
        initial?.inputPayload ? JSON.stringify(initial.inputPayload, null, 2) : ''
    );
    const [payloadError, setPayloadError] = useState<string | null>(null);
    const [confirmSave, setConfirmSave] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [capabilities, setCapabilities] = useState<SchedulerCapabilityItem[]>([]);
    const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
    const [methodOptions, setMethodOptions] = useState<string[]>([]);
    const [methodsLoading, setMethodsLoading] = useState(false);
    const [methodsLoadedForRef, setMethodsLoadedForRef] = useState<string | null>(null);
    const [selectedCapability, setSelectedCapability] = useState<SelectedCapability | null>(
        initial?.targetRef
            ? {
                  ref: initial.targetRef,
                  name: initial.targetRef,
                  type: 'tool',
              }
            : null,
    );
    const [schedule, setSchedule] = useState<ScheduleOutput>({
        triggerType: (initial?.triggerType as ScheduleOutput['triggerType']) ?? 'recurring',
        scheduleKind: (initial?.scheduleKind as ScheduleOutput['scheduleKind']) ?? 'cron',
        cronExpression: initial?.cronExpression ?? '',
        rruleExpression: initial?.rruleExpression ?? '',
        timezone: initial?.timezone ?? 'UTC',
        delayUntilAt: initial?.delayUntilAt,
    });

    const currentStepIndex = WIZARD_STEPS.findIndex((s) => s.id === currentStep);

    const loadCapabilityMethods = async (capabilityRef: string, fallbackMethods: string[] = []) => {
        const fallback = Array.from(new Set(fallbackMethods.filter(Boolean)));
        const parts = capabilityRef.includes('/') ? capabilityRef.split('/', 2) : null;
        const capAppKey = parts ? parts[0] : '';
        const capName = parts ? parts[1] : capabilityRef;

        if (!capAppKey || !capName) {
            setMethodOptions(fallback);
            setTargetMethod((prev) => (fallback.includes(prev) ? prev : fallback[0] ?? ''));
            setMethodsLoadedForRef(capabilityRef);
            return;
        }

        setMethodsLoading(true);
        try {
            const res = await api.get<{
                working_revision?: { methods?: Array<{ name?: string }> };
                methods?: Array<{ name?: string }>;
            }>(`/applications/${encodeURIComponent(capAppKey)}/capabilities/${encodeURIComponent(capName)}`);
            const fetched = (res.data?.working_revision?.methods ?? res.data?.methods ?? [])
                .map((method) => method?.name ?? '')
                .filter((method): method is string => Boolean(method));
            const nextMethods = Array.from(new Set([...fallback, ...fetched]));
            setMethodOptions(nextMethods);
            setTargetMethod((prev) => (nextMethods.includes(prev) ? prev : nextMethods[0] ?? ''));
        } catch {
            setMethodOptions(fallback);
            setTargetMethod((prev) => (fallback.includes(prev) ? prev : fallback[0] ?? ''));
        } finally {
            setMethodsLoading(false);
            setMethodsLoadedForRef(capabilityRef);
        }
    };

    useEffect(() => {
        setCapabilitiesLoading(true);
        api.get('/capabilities', { params: { per_page: 500, include_entitled: 'true' } })
            .then((res) => {
                const items = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
                setCapabilities(
                    items.map((item: Record<string, unknown>) => {
                        const rootMethods = Array.isArray(item.methods)
                            ? item.methods
                            : [];
                        const workingRevision = item.working_revision as
                            | { methods?: unknown[] }
                            | undefined;
                        const revisionMethods = Array.isArray(workingRevision?.methods)
                            ? workingRevision.methods
                            : [];
                        const methodNames = [...rootMethods, ...revisionMethods]
                            .map((method) => {
                                if (typeof method === 'string') return method;
                                if (method && typeof method === 'object' && 'name' in method) {
                                    const methodName = (method as { name?: unknown }).name;
                                    return typeof methodName === 'string' ? methodName : '';
                                }
                                return '';
                            })
                            .filter((method): method is string => Boolean(method));

                        return {
                            name: item.name as string,
                            description: (item.description as string) ?? undefined,
                            tags: (item.tags as string[]) ?? [],
                            fqcn: item.app_key
                                ? `${item.app_key}/${item.name}`
                                : (item.name as string),
                            app_key: item.app_key as string,
                            visibility: (item.visibility as string) ?? 'account',
                            created_by: item.created_by as string,
                            organization_id: item.organization_id as string,
                            status: (item.status as string) ?? 'active',
                            type: (item.type as string) ?? 'tool',
                            methodNames: Array.from(new Set(methodNames)),
                        };
                    }),
                );
            })
            .catch(() => setCapabilities([]))
            .finally(() => setCapabilitiesLoading(false));
    }, []);

    useEffect(() => {
        if (!targetRef.trim() || methodsLoadedForRef === targetRef) return;
        const found = capabilities.find((c) => (c.fqcn ?? c.name) === targetRef);
        if (found) {
            setSelectedCapability({
                ref: targetRef,
                name: found.name ?? targetRef,
                type: found.type ?? 'tool',
                description: found.description ?? undefined,
            });
        }
        void loadCapabilityMethods(targetRef, found?.methodNames ?? []);
    }, [targetRef, capabilities, methodsLoadedForRef]);

    const handleCapabilitySelect = (fqcnOrName: string) => {
        const found = capabilities.find((c) => (c.fqcn ?? c.name) === fqcnOrName);
        setSelectedCapability({
            ref: fqcnOrName,
            name: found?.name ?? fqcnOrName,
            type: found?.type ?? 'tool',
            description: found?.description ?? undefined,
        });
        setTargetRef(fqcnOrName);
        setMethodsLoadedForRef(null);
        setMethodOptions([]);
        setTargetMethod('');
        setPickerOpen(false);
    };

    const handleCapabilityRemove = () => {
        setSelectedCapability(null);
        setTargetRef('');
        setTargetMethod('');
        setMethodOptions([]);
        setMethodsLoadedForRef(null);
    };

    const isLastStep = currentStep === 'summary';

    const parsePayload = (): Record<string, unknown> | undefined | null => {
        if (payloadStr.trim()) {
            try {
                setPayloadError(null);
                return JSON.parse(payloadStr);
            } catch {
                setPayloadError('Input payload must be valid JSON.');
                return null;
            }
        }
        setPayloadError(null);
        return undefined;
    };

    const isPayloadValid = useMemo(() => {
        if (!payloadStr.trim()) return true;
        try {
            JSON.parse(payloadStr);
            return true;
        } catch {
            return false;
        }
    }, [payloadStr]);

    const canGoNext = useMemo(() => {
        if (currentStep === 'details') {
            return Boolean(name.trim()) && Boolean(targetRef.trim()) && Boolean(targetMethod.trim());
        }
        if (currentStep === 'payload') {
            return isPayloadValid;
        }
        return true;
    }, [currentStep, name, targetRef, targetMethod, isPayloadValid]);

    const goToNextStep = () => {
        if (currentStep === 'details') {
            if (!name.trim() || !targetRef.trim() || !targetMethod.trim()) return;
            setCurrentStep('schedule');
            return;
        }
        if (currentStep === 'schedule') {
            setCurrentStep('payload');
            return;
        }
        if (currentStep === 'payload') {
            if (parsePayload() === null) return;
            setCurrentStep('summary');
        }
    };

    const goToPreviousStep = () => {
        if (currentStep === 'schedule') setCurrentStep('details');
        if (currentStep === 'payload') setCurrentStep('schedule');
        if (currentStep === 'summary') setCurrentStep('payload');
    };

    const handleSubmit = async () => {
        const inputPayload = parsePayload();
        if (inputPayload === null) {
            setCurrentStep('payload');
            return;
        }
        await onSave({
            name,
            triggerType: schedule.triggerType,
            scheduleKind: schedule.scheduleKind,
            targetRef,
            targetMethod,
            cronExpression: schedule.cronExpression || undefined,
            rruleExpression: schedule.rruleExpression || undefined,
            timezone: schedule.timezone,
            concurrencyPolicy,
            misfirePolicy,
            delayUntilAt: schedule.delayUntilAt,
            inputPayload,
        });
    };

    const scheduleSummary = useMemo(() => {
        if (schedule.triggerType === 'one_shot') {
            return schedule.delayUntilAt
                ? `One-shot at ${new Date(schedule.delayUntilAt).toLocaleString()}`
                : 'One-shot trigger';
        }
        if (schedule.scheduleKind === 'cron') {
            return schedule.cronExpression ? `Cron: ${schedule.cronExpression}` : 'Recurring (cron)';
        }
        if (schedule.scheduleKind === 'rrule') {
            return schedule.rruleExpression ? `RRULE: ${schedule.rruleExpression}` : 'Recurring (rrule)';
        }
        return 'Recurring schedule';
    }, [schedule]);

    const capColors = selectedCapability
        ? getCapabilityColors(selectedCapability.type)
        : null;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2">
                {WIZARD_STEPS.map((step, index) => (
                    <div key={step.id} className="flex items-center">
                        <div
                            className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                                index < currentStepIndex
                                    ? 'bg-emerald-500 text-white'
                                    : index === currentStepIndex
                                      ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500'
                                      : 'bg-slate-100 text-slate-400'
                            }`}
                        >
                            {index < currentStepIndex ? <Check size={14} /> : index + 1}
                        </div>
                        {index < WIZARD_STEPS.length - 1 && (
                            <div
                                className={`mx-2 h-0.5 w-10 ${
                                    index < currentStepIndex ? 'bg-emerald-500' : 'bg-slate-200'
                                }`}
                            />
                        )}
                    </div>
                ))}
            </div>
            <div>
                <h3 className="text-base font-semibold text-slate-900">
                    {WIZARD_STEPS[currentStepIndex]?.title}
                </h3>
                <p className="text-sm text-slate-500">
                    {WIZARD_STEPS[currentStepIndex]?.description}
                </p>
            </div>

            {currentStep === 'details' && (
                <Card className="border-border/50">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium">Basic Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 md:grid-cols-5">
                            <div className="space-y-4 md:col-span-3">
                                <div className="space-y-2">
                                    <Label>Name</Label>
                                    <input
                                        type="text"
                                        required
                                        className={inputClassName}
                                        placeholder="Daily report generation"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Target Capability</Label>
                                    {selectedCapability ? (
                                        <Card
                                            className={cn(
                                                'relative border-2 p-3 transition-all duration-200',
                                                'border-l-4 border-slate-200 bg-white hover:shadow-md',
                                            )}
                                            style={{ borderLeftColor: capColors?.hex }}
                                        >
                                            <div className="flex flex-row items-start gap-3">
                                                <div
                                                    className={cn(
                                                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                                                        capColors?.bgLight,
                                                        capColors?.text,
                                                    )}
                                                >
                                                    {capColors && <capColors.icon size={16} />}
                                                </div>
                                                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                                    <span
                                                        className={cn(
                                                            'text-[10px] font-medium uppercase tracking-wide',
                                                            capColors?.text ?? 'text-slate-500',
                                                        )}
                                                    >
                                                        {selectedCapability.type}
                                                    </span>
                                                    <span className="truncate text-sm font-bold text-slate-900">
                                                        {selectedCapability.name}
                                                    </span>
                                                    <span className="truncate font-mono text-[10px] text-slate-400">
                                                        {selectedCapability.ref}
                                                    </span>
                                                    {selectedCapability.description && (
                                                        <span className="line-clamp-2 text-[10px] leading-tight text-slate-500">
                                                            {selectedCapability.description}
                                                        </span>
                                                    )}
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-5 w-5 shrink-0 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-500"
                                                    onClick={handleCapabilityRemove}
                                                    aria-label="Remove capability"
                                                >
                                                    <X size={10} />
                                                </Button>
                                            </div>
                                        </Card>
                                    ) : (
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setPickerOpen(true)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setPickerOpen(true);
                                                }
                                            }}
                                            className="cursor-pointer"
                                        >
                                            <Card className="border-2 border-dashed border-slate-200 bg-slate-50/50 p-3">
                                                <div className="flex flex-col items-center justify-center py-3">
                                                    <span className="mb-1 text-slate-400">
                                                        <Layers size={24} />
                                                    </span>
                                                    <div className="flex items-center gap-1.5">
                                                        <Plus size={14} className="text-slate-500" />
                                                        <span className="text-xs font-medium text-slate-500">
                                                            Click to select a capability
                                                        </span>
                                                    </div>
                                                </div>
                                            </Card>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Method</Label>
                                    <select
                                        required
                                        className={inputClassName}
                                        disabled={!selectedCapability || methodsLoading || methodOptions.length === 0}
                                        value={targetMethod}
                                        onChange={(e) => setTargetMethod(e.target.value)}
                                    >
                                        {!selectedCapability ? (
                                            <option value="">Select a capability first</option>
                                        ) : methodsLoading ? (
                                            <option value="">Loading methods...</option>
                                        ) : methodOptions.length === 0 ? (
                                            <option value="">No methods available</option>
                                        ) : (
                                            <>
                                                <option value="">Select a method</option>
                                                {methodOptions.map((methodName) => (
                                                    <option key={methodName} value={methodName}>
                                                        {methodName}
                                                    </option>
                                                ))}
                                            </>
                                        )}
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-3 rounded-lg border border-border/50 p-4 md:col-span-2">
                                <h4 className="text-sm font-medium text-slate-700">Policies</h4>
                                <div className="space-y-2">
                                    <Label>Concurrency</Label>
                                    <select
                                        className={inputClassName}
                                        value={concurrencyPolicy}
                                        onChange={(e) => setConcurrencyPolicy(e.target.value)}
                                    >
                                        <option value="allow">Allow</option>
                                        <option value="forbid">Forbid</option>
                                        <option value="queue">Queue</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Misfire</Label>
                                    <select
                                        className={inputClassName}
                                        value={misfirePolicy}
                                        onChange={(e) => setMisfirePolicy(e.target.value)}
                                    >
                                        <option value="fire_once_now">Fire Once Now</option>
                                        <option value="catch_up_all">Catch Up All</option>
                                        <option value="skip">Skip</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {currentStep === 'schedule' && (
                <ScheduleBuilder value={schedule} onChange={setSchedule} />
            )}

            {currentStep === 'payload' && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium">Input Payload (JSON)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <textarea
                            className="flex min-h-[140px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm font-mono"
                            placeholder='{"key": "value"}'
                            value={payloadStr}
                            onChange={(e) => {
                                setPayloadStr(e.target.value);
                                if (payloadError) setPayloadError(null);
                            }}
                        />
                        {payloadError && (
                            <p className="text-xs text-red-500">{payloadError}</p>
                        )}
                    </CardContent>
                </Card>
            )}

            {currentStep === 'summary' && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium">Review Trigger</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <p><span className="font-medium">Name:</span> {name}</p>
                        <p><span className="font-medium">Target:</span> {targetRef}</p>
                        <p><span className="font-medium">Method:</span> {targetMethod || '—'}</p>
                        <p><span className="font-medium">Schedule:</span> {scheduleSummary}</p>
                        <p><span className="font-medium">Timezone:</span> {schedule.timezone || 'UTC'}</p>
                        <p><span className="font-medium">Concurrency:</span> {concurrencyPolicy}</p>
                        <p><span className="font-medium">Misfire:</span> {misfirePolicy}</p>
                        <div>
                            <span className="font-medium">Payload:</span>
                            <pre className="mt-1 rounded-md bg-slate-50 p-3 text-xs">
                                {payloadStr.trim() ? payloadStr : '(none)'}
                            </pre>
                        </div>
                        <label className="mt-2 flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={confirmSave}
                                onChange={(e) => setConfirmSave(e.target.checked)}
                            />
                            I confirm these trigger changes are correct.
                        </label>
                    </CardContent>
                </Card>
            )}

            <div className="flex items-center justify-between">
                <div>
                    {currentStepIndex > 0 && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={goToPreviousStep}
                            disabled={saving}
                        >
                            <ChevronLeft size={16} className="mr-1" />
                            Back
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {!isLastStep ? (
                        <Button
                            type="button"
                            onClick={goToNextStep}
                            disabled={saving || !canGoNext}
                        >
                            Next
                            <ChevronRight size={16} className="ml-1" />
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            onClick={() => void handleSubmit()}
                            disabled={saving || !confirmSave}
                        >
                            {saving ? 'Saving...' : 'Save Trigger'}
                        </Button>
                    )}
                </div>
            </div>
            <CapabilityPickerDrawer
                isOpen={pickerOpen}
                onClose={() => setPickerOpen(false)}
                slotType="tool"
                items={capabilities}
                selectedRefs={selectedCapability ? [selectedCapability.ref] : []}
                onSelect={handleCapabilitySelect}
                loading={capabilitiesLoading}
            />
        </div>
    );
}
